import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineTrash } from "react-icons/hi";

import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import pdfIcon from "../../assets/svg/pdf-icon.svg";
import {
  FOLLOW_UP_ADDRESSEE_DESIGNATIONS,
  FOLLOW_UP_APPLICANT_TYPES,
  FOLLOW_UP_APPLICATION_STATUS_VALUES,
  FOLLOW_UP_CLASS_NUMBERS,
  FOLLOW_UP_FORM_OF_ADDRESS_OPTIONS,
  FOLLOW_UP_PLACE_OPTIONS,
  FOLLOW_UP_TYPE_OF_MARK_OPTIONS,
  FOLLOW_UP_USER_DATE_TYPES,
  generateFollowUpAnalysisReportPdf,
  generateFollowUpUploadPresignedUrls,
  getFollowUpAnalysisReportPreview,
  getFollowUpDiscussionDetails,
  upsertFollowUpAnalysisReport,
  type FollowUpAnalysisReportApplicationInput,
  type FollowUpAnalysisReportPreview,
  type FollowUpUserDateType,
} from "../../api/followUpDiscussion";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

const FORM_STYLES = {
  fontFamily: "Inter, sans-serif",
  fontFeatureSettings: "'ss01' on, 'cv01' on",
} as const;

const LABEL_CLASS =
  "text-[12px] font-normal leading-5 text-[#222222]";
const INPUT_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const TEXTAREA_CLASS =
  "w-full min-h-[64px] px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border resize-y";
const READONLY_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px] box-border";

const ACCEPTED_IMAGE_TYPES = ".png,.jpg,.jpeg,.webp";
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

function parseDateInput(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseOrdinalDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const m = s.match(
    /^(\d{1,2})(?:st|nd|rd|th)?\s+day\s+of\s+([A-Za-z]+)\s+(\d{4})$/i
  );
  if (!m) return null;
  const day = Number(m[1]);
  const monthIdx = months.findIndex(
    (mo) => mo.toLowerCase() === m[2].toLowerCase()
  );
  if (monthIdx < 0) return null;
  const date = new Date(Number(m[3]), monthIdx, day);
  return isNaN(date.getTime()) ? null : date;
}

type EditableApplication = {
  rowId: string;
  id?: string;
  tmNo: string;
  dateOfFiling: Date | null;
  mark: string;
  typeOfMark: string;
  classNumber: string;
  goodsServices: string;
  imageOfMarkFileKey: string | null;
  imageOfMarkUrl: string | null;
  pendingImageFile: File | null;
  pendingImagePreviewUrl: string | null;
  userDateType: FollowUpUserDateType | "";
  userDate: Date | null;
  status: string;
};

let rowIdCounter = 0;
function makeRowId(): string {
  rowIdCounter += 1;
  return `row-${rowIdCounter}-${Date.now()}`;
}

function makeEmptyApplication(): EditableApplication {
  return {
    rowId: makeRowId(),
    id: undefined,
    tmNo: "",
    dateOfFiling: null,
    mark: "",
    typeOfMark: "",
    classNumber: "",
    goodsServices: "",
    imageOfMarkFileKey: null,
    imageOfMarkUrl: null,
    pendingImageFile: null,
    pendingImagePreviewUrl: null,
    userDateType: "",
    userDate: null,
    status: "",
  };
}

type FollowUpAnalysisReportViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export const FOLLOW_UP_ANALYSIS_REPORT_FORM_ID =
  "follow-up-analysis-report-form";

export default function FollowUpAnalysisReportView({
  leadId,
  onSuccess,
}: FollowUpAnalysisReportViewProps) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [applications, setApplications] = useState<EditableApplication[]>([]);

  const [mark, setMark] = useState("");
  const [textOfReport, setTextOfReport] = useState("");
  const [applicant, setApplicant] = useState("");
  const [applicantAddress, setApplicantAddress] = useState("");
  const [applicantType, setApplicantType] = useState("");
  const [addresseeDesignation, setAddresseeDesignation] = useState("");
  const [addresseeCustomName, setAddresseeCustomName] = useState("");
  const [addresseeCustomNameError, setAddresseeCustomNameError] = useState(false);
  const [firmName, setFirmName] = useState("");
  const [place, setPlace] = useState("");
  const [isManualPlace, setIsManualPlace] = useState(false);
  const [districtOfApplicant, setDistrictOfApplicant] = useState("");
  const [formOfAddress, setFormOfAddress] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");
  const [dateOfReport, setDateOfReport] = useState<Date | null>(null);
  const [reportNo, setReportNo] = useState("");

  const [pdf, setPdf] = useState<{ s3Key: string; downloadUrl: string; filename: string } | null>(
    null
  );
  const [filePreview, setFilePreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const blobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      blobUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFollowUpDiscussionDetails(leadId),
      getFollowUpAnalysisReportPreview(leadId).catch(() => null),
    ])
      .then(([details, preview]) => {
        if (cancelled) return;

        const apps: EditableApplication[] = (details.applications ?? []).map(
          (a): EditableApplication => ({
            rowId: makeRowId(),
            id: a.id,
            tmNo: a.tmNo ?? "",
            dateOfFiling: parseDateInput(a.dateOfFiling),
            mark: a.mark ?? "",
            typeOfMark: a.typeOfMark ?? "",
            classNumber:
              a.classNumber != null ? String(a.classNumber) : "",
            goodsServices: a.goodsServices ?? "",
            imageOfMarkFileKey: null,
            imageOfMarkUrl: null,
            pendingImageFile: null,
            pendingImagePreviewUrl: null,
            userDateType: (a.userDateType as FollowUpUserDateType | null) ?? "",
            userDate: parseDateInput(a.userDate),
            status: a.status ?? "",
          })
        );
        setApplications(apps.length > 0 ? apps : [makeEmptyApplication()]);
        setMark(details?.preliminary?.markName ?? "")

        if (preview) {
          setTextOfReport(preview.textOfReport ?? "");
          setApplicant(preview.applicant ?? "");
          setApplicantAddress(preview.applicantAddress ?? "");
          setApplicantType(preview.applicantType ?? "");
          setFormOfAddress(preview.formOfAddress ?? "");
          setReportNo(preview.reportNo ?? "");
          setSignatoryName(preview.signatoryName ?? "");
          setSignatoryDesignation(preview.signatoryDesignation ?? "");
          const parsed = parseOrdinalDate(preview.dateOfReport);
          setDateOfReport(parsed);
          setFirmName(preview.firmName ?? "");
          setPlace(preview.place ?? "");
          setDistrictOfApplicant(preview.districtOfApplicant ?? "");
          setIsManualPlace(
            !!preview.place &&
            !(FOLLOW_UP_PLACE_OPTIONS as readonly string[]).includes(preview.place)
          );           
          setAddresseeDesignation(preview.designation ?? "");
          setAddresseeCustomName(preview.designationCustomName ?? "");


          // Apply per-application status from preview if richer.
          type PreviewApp =
            FollowUpAnalysisReportPreview["applications"][number];
          const previewByTm = new Map<string, PreviewApp>(
            preview.applications.map((p: PreviewApp) => [p.tmNo.trim(), p])
          );
          setApplications((prev) =>
            prev.map((row) => {
              const match = previewByTm.get(row.tmNo.trim());
              if (!match) return row;
              return {
                ...row,
                status: row.status || match.status || "",
                imageOfMarkFileKey:
                  row.imageOfMarkFileKey ?? match.imageOfMarkFileKey ?? null,
                imageOfMarkUrl:
                  row.imageOfMarkUrl ?? match.imageOfMarkUrl ?? null,
              };
            })
          );

          if (
            preview.reportPdfKey &&
            preview.reportPdfUrl &&
            preview.reportPdfFilename
          ) {
            setPdf({
              s3Key: preview.reportPdfKey,
              downloadUrl: preview.reportPdfUrl,
              filename: preview.reportPdfFilename,
            });
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load analysis report"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const updateApplication = (
    rowId: string,
    patch: Partial<EditableApplication>
  ) => {
    setApplications((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
    setPdf(null);
  };

  const addApplicationRow = () => {
    setApplications((prev) => [...prev, makeEmptyApplication()]);
    setPdf(null);
  };

  /**
   * Resize the applications list to `count` rows. Grow by appending empty rows;
   * shrink by trimming from the end while revoking blob URLs of removed rows so
   * we don't leak object URLs for previewed mark images.
   */
  const setApplicationCount = (count: number) => {
    if (!Number.isFinite(count) || count < 1) return;
    setApplications((prev) => {
      if (count === prev.length) return prev;
      if (count > prev.length) {
        const next = [...prev];
        for (let i = prev.length; i < count; i++) {
          next.push(makeEmptyApplication());
        }
        return next;
      }
      const dropped = prev.slice(count);
      for (const row of dropped) {
        if (row.pendingImagePreviewUrl) {
          URL.revokeObjectURL(row.pendingImagePreviewUrl);
          blobUrlsRef.current = blobUrlsRef.current.filter(
            (u) => u !== row.pendingImagePreviewUrl
          );
        }
      }
      return prev.slice(0, count);
    });
    setPdf(null);
  };

  const removeApplicationRow = (rowId: string) => {
    setApplications((prev) => {
      if (prev.length <= 1) return prev;
      const target = prev.find((r) => r.rowId === rowId);
      if (target?.pendingImagePreviewUrl) {
        URL.revokeObjectURL(target.pendingImagePreviewUrl);
        blobUrlsRef.current = blobUrlsRef.current.filter(
          (u) => u !== target.pendingImagePreviewUrl
        );
      }
      return prev.filter((r) => r.rowId !== rowId);
    });
    setPdf(null);
  };

  const handleImageSelect = (rowId: string, file: File) => {
    if (!file.type.startsWith("image/") && !/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error("Please select an image file (PNG, JPG, or WEBP).");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error("Image must be 10MB or smaller.");
      return;
    }
    const url = URL.createObjectURL(file);
    blobUrlsRef.current.push(url);
    setApplications((prev) =>
      prev.map((row) => {
        if (row.rowId !== rowId) return row;
        if (row.pendingImagePreviewUrl) {
          URL.revokeObjectURL(row.pendingImagePreviewUrl);
          blobUrlsRef.current = blobUrlsRef.current.filter(
            (u) => u !== row.pendingImagePreviewUrl
          );
        }
        return {
          ...row,
          pendingImageFile: file,
          pendingImagePreviewUrl: url,
          imageOfMarkFileKey: null,
          imageOfMarkUrl: null,
        };
      })
    );
    setPdf(null);
  };

  const validate = (): boolean => {
    if (applications.length === 0) {
      toast.error("Add at least one application.");
      return false;
    }
    for (let i = 0; i < applications.length; i++) {
      const a = applications[i];
      if (!a.tmNo.trim()) {
        toast.error(`Application ${i + 1}: TM Application No. is required.`);
        return false;
      }
      if (a.classNumber.trim()) {
        const num = Number(a.classNumber);
        if (!Number.isFinite(num) || num <= 0) {
          toast.error(
            `Application ${i + 1}: Class must be a positive number.`
          );
          return false;
        }
      }
      if (
        a.userDateType === FOLLOW_UP_USER_DATE_TYPES.SpecificDate &&
        !a.userDate
      ) {
        toast.error(
          `Application ${i + 1}: User Date is required when type is Specific Date.`
        );
        return false;
      }
    }
    if (
      addresseeDesignation === "NAME" &&
      !addresseeCustomName.trim()
    ) {
      setAddresseeCustomNameError(true);
      toast.error('Addressee custom name is required when designation is "NAME".');
      return false;
    }
    return true;
  };

  const uploadPendingImages = async (): Promise<EditableApplication[]> => {
    if (!leadId) return applications;
    const rowsNeedingUpload = applications.filter((r) => r.pendingImageFile);
    if (rowsNeedingUpload.length === 0) return applications;

    const presigned = await generateFollowUpUploadPresignedUrls(
      leadId,
      rowsNeedingUpload.map((r) => ({
        filename: r.pendingImageFile!.name,
        mimeType: r.pendingImageFile!.type || "image/png",
      }))
    );

    if (presigned.length !== rowsNeedingUpload.length) {
      throw new Error("Failed to generate upload URLs for all mark images");
    }

    const keyByRowId = new Map<string, string>();
    for (let i = 0; i < rowsNeedingUpload.length; i++) {
      const row = rowsNeedingUpload[i];
      const target = presigned[i];
      const file = row.pendingImageFile!;
      const putRes = await fetch(target.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "image/png" },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload mark image ${file.name}`);
      }
      keyByRowId.set(row.rowId, target.key);
    }

    return applications.map((r) =>
      keyByRowId.has(r.rowId)
        ? {
            ...r,
            imageOfMarkFileKey: keyByRowId.get(r.rowId) ?? r.imageOfMarkFileKey,
            pendingImageFile: null,
          }
        : r
    );
  };

  const buildPayload = (rows: EditableApplication[]) => ({
    addresseeDesignation: addresseeDesignation
      ? (addresseeDesignation as
          | "The Proprietor"
          | "The Managing Partner"
          | "The Designated Partner"
          | "The Managing Director"
          | "The Managing Trustee"
          | "The Chairman"
          | "NAME")
      : undefined,
    addresseeCustomName:
      addresseeDesignation === "NAME"
        ? addresseeCustomName.trim() || null
        : null,
    firmName: firmName.trim() || null,
    place: place.trim() || null,
    formOfAddress: formOfAddress || null,
    applicant: applicant || null,
    applicantAddress: applicantAddress || null,
    applicantType: applicantType || null,
    signatoryName: signatoryName.trim() || null,
    signatoryDesignation: signatoryDesignation.trim() || null,
    districtOfApplicant: districtOfApplicant || null,
    textOfReport: textOfReport || null,
    dateOfReport: toISODateString(dateOfReport),
    applications: rows.map((a, idx) => {
      const item: FollowUpAnalysisReportApplicationInput = {
        tmNo: a.tmNo.trim(),
        dateOfFiling: toISODateString(a.dateOfFiling),
        mark: mark || null,
        typeOfMark: a.typeOfMark || null,
        classNumber: a.classNumber ? Number(a.classNumber) : null,
        goodsServices: a.goodsServices || null,
        imageOfMarkFileKey: a.imageOfMarkFileKey ?? null,
        imageOfMark: a.imageOfMarkFileKey ?? null,
        userDateType: a.userDateType || null,
        userDate:
          a.userDateType === FOLLOW_UP_USER_DATE_TYPES.SpecificDate
            ? toISODateString(a.userDate)
            : null,
        status: a.status || null,
        sortOrder: idx,
      };
      if (a.id) item.id = a.id;
      return item;
    }),
  });

  const persistForm = async (
    options: { advance?: boolean } = {}
  ): Promise<boolean> => {
    const { advance = true } = options;
    if (!leadId || saving) return false;
    if (!validate()) return false;

    setSaving(true);
    try {
      const rowsAfterUpload = await uploadPendingImages();
      setApplications(rowsAfterUpload);
      await upsertFollowUpAnalysisReport(leadId, buildPayload(rowsAfterUpload));
      toast.success("Analysis report saved successfully.");
      // Refresh preview to get latest reportNo / dateOfReport ordinal.
      try {
        const preview = await getFollowUpAnalysisReportPreview(leadId);
        if (preview.reportNo) setReportNo(preview.reportNo);
      } catch {
        // Non-critical — ignore preview reload errors.
      }
      if (advance) onSuccess?.(leadId);
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save analysis report"
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await persistForm({ advance: true });
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generatingPdf) return;
    const ok = await persistForm({ advance: false });
    if (!ok) return;
    setGeneratingPdf(true);
    try {
      const result = await generateFollowUpAnalysisReportPdf(leadId);
      setPdf({
        s3Key: result.s3Key,
        downloadUrl: result.downloadUrl,
        filename: result.filename,
      });
      toast.success("Analysis report PDF generated.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!pdf?.downloadUrl) {
      toast.error("Generate PDF first before downloading.");
      return;
    }
    setDownloadingPdf(true);
    try {
      await downloadFileFromUrlWithFallback(pdf.downloadUrl, pdf.filename);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePreviewPdf = () => {
    if (!pdf?.downloadUrl) {
      toast.error("Generate PDF first before previewing.");
      return;
    }
    setFilePreview({
      url: pdf.downloadUrl,
      name: pdf.filename,
      kind: detectPreviewKind(pdf.filename, "application/pdf"),
    });
  };

  const handleClosePreview = () => {
    if (filePreview?.revokeOnClose) URL.revokeObjectURL(filePreview.url);
    setFilePreview(null);
  };

  const previewMarks = useMemo(() => {
    const marks = applications
      .map((a) => a.mark.trim())
      .filter((m): m is string => Boolean(m));
    return Array.from(new Set(marks));
  }, [applications]);

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">Loading analysis report...</p>
      </div>
    );
  }

  const showCustomNameField = addresseeDesignation === "NAME";

  return (
    <form
      id={FOLLOW_UP_ANALYSIS_REPORT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={FORM_STYLES}
    >
      {/* Header summary: Mark | Text of Report | No of Applications */}
      <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-5">
       <div className="flex flex-col flex-1 min-w-0 gap-1">
        <label className={LABEL_CLASS}>Mark</label>
        <input
          type="text"
          value={mark}
          readOnly
          className={READONLY_CLASS}
        />
      </div>
        <div className="flex flex-col flex-1 min-w-0 gap-1">
          <label className={LABEL_CLASS}>Text of Report</label>
          <textarea
            value={textOfReport}
            onChange={(e) => {
              setTextOfReport(e.target.value);
              setPdf(null);
            }}
            placeholder="Status of the applications has been reviewed..."
            className={TEXTAREA_CLASS}
            rows={2}
          />
        </div>
        <div className="flex flex-col flex-1 min-w-0 gap-1">
          <label className={LABEL_CLASS}>No of Applications</label>
          <Select
            value={String(applications.length)}
            onValueChange={(v) => setApplicationCount(Number(v))}
          >
            <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
              <SelectValue placeholder="Select count" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {String(n).padStart(2, "0")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Applications list */}
      <div className="flex flex-col gap-4">
        {applications.map((app, idx) => (
          <div
            key={app.rowId}
            className="flex flex-col gap-4 p-4 rounded-[8px] border border-[#EEEEEE] bg-white"
          >
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-medium text-[#222222]">
                Application {idx + 1}
              </span>
              {applications.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeApplicationRow(app.rowId)}
                  className="inline-flex items-center gap-1 text-[12px] text-[#6D6D6D] hover:text-[#D92D20]"
                  aria-label="Remove application"
                >
                  <HiOutlineTrash className="w-4 h-4" />
                  Remove
                </button>
              )}
            </div>

            {/* Row: TM No | Date of Filing | Mark (from API) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              <div className="flex flex-col gap-1 min-w-0">
                <label className={LABEL_CLASS}>Application Number (TM No.)</label>
                <input
                  type="text"
                  value={app.tmNo}
                  onChange={(e) =>
                    updateApplication(app.rowId, { tmNo: e.target.value })
                  }
                  placeholder="Text Here"
                  className={INPUT_CLASS}
                />
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <label className={LABEL_CLASS}>Date of Filing</label>
                <DateTimePicker
                  value={app.dateOfFiling}
                  onChange={(d) =>
                    updateApplication(app.rowId, { dateOfFiling: d })
                  }
                  placeholder="Select Date"
                  showTime={false}
                  buttonClassName="!h-9 !min-h-0 !text-[12px]"
                />
              </div>
              
            </div>

            {/* Row: Goods/Services | Class | Type of Mark | User Date */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
              <div className="flex flex-col gap-1 min-w-0">
                <label className={LABEL_CLASS}>Goods / Services</label>
                <input
                  type="text"
                  value={app.goodsServices}
                  onChange={(e) =>
                    updateApplication(app.rowId, {
                      goodsServices: e.target.value,
                    })
                  }
                  placeholder="Goods / Services"
                  className={INPUT_CLASS}
                />
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <label className={LABEL_CLASS}>Selected Class</label>
                <Select
                  value={app.classNumber || undefined}
                  onValueChange={(v) =>
                    updateApplication(app.rowId, { classNumber: v })
                  }
                >
                  <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                    <SelectValue placeholder="Select Class" />
                  </SelectTrigger>
                  <SelectContent>
                    {FOLLOW_UP_CLASS_NUMBERS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {`Class ${n}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <label className={LABEL_CLASS}>Type of Mark</label>
                <Select
                  value={app.typeOfMark || undefined}
                  onValueChange={(v) =>
                    updateApplication(app.rowId, { typeOfMark: v })
                  }
                >
                  <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                    <SelectValue placeholder="Select Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FOLLOW_UP_TYPE_OF_MARK_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <label className={LABEL_CLASS}>User Date Type</label>
                <Select
                  value={app.userDateType || undefined}
                  onValueChange={(v) =>
                    updateApplication(app.rowId, {
                      userDateType: v as FollowUpUserDateType,
                      userDate:
                        v === FOLLOW_UP_USER_DATE_TYPES.SpecificDate
                          ? app.userDate
                          : null,
                    })
                  }
                >
                  <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={FOLLOW_UP_USER_DATE_TYPES.ProposedToBeUsed}>
                      Proposed to be used
                    </SelectItem>
                    <SelectItem value={FOLLOW_UP_USER_DATE_TYPES.SpecificDate}>
                      Specific Date
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row: User Date (only when SPECIFIC_DATE) | Status */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-5">
              {app.userDateType === FOLLOW_UP_USER_DATE_TYPES.SpecificDate && (
                <div className="flex flex-col flex-1 min-w-0 gap-1">
                  <label className={LABEL_CLASS}>User Date</label>
                  <DateTimePicker
                    value={app.userDate}
                    onChange={(d) =>
                      updateApplication(app.rowId, { userDate: d })
                    }
                    placeholder="Select Date"
                    showTime={false}
                    buttonClassName="!h-9 !min-h-0 !text-[12px]"
                  />
                </div>
              )}
              <div className="flex flex-col flex-1 min-w-0 gap-1">
                <label className={LABEL_CLASS}>Status</label>
                <Select
                  value={app.status || undefined}
                  onValueChange={(v) =>
                    updateApplication(app.rowId, { status: v })
                  }
                >
                  <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {FOLLOW_UP_APPLICATION_STATUS_VALUES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Image of Mark */}
            <div className="flex flex-col gap-1">
              <label className={LABEL_CLASS}>Image of Mark</label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC]">
                <div className="flex-1 min-w-0">
                  {app.pendingImagePreviewUrl ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={app.pendingImagePreviewUrl}
                        alt="Mark preview"
                        className="w-16 h-16 object-contain rounded-[6px] border border-[#EEEEEE] bg-white"
                      />
                      <span className="text-[12px] text-[#16A34A] truncate">
                        {app.pendingImageFile?.name ?? "Selected image"}
                      </span>
                    </div>
                  ) : app.imageOfMarkUrl ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={app.imageOfMarkUrl}
                        alt="Mark"
                        className="w-16 h-16 object-contain rounded-[6px] border border-[#EEEEEE] bg-white"
                      />
                      <span className="text-[12px] text-[#16A34A] truncate">
                        Image attached
                      </span>
                    </div>
                  ) : app.imageOfMarkFileKey ? (
                    <span className="text-[12px] text-[#16A34A] truncate">
                      Image attached
                    </span>
                  ) : (
                    <span className="text-[12px] text-[#6D6D6D]">
                      No image selected
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {(app.pendingImagePreviewUrl || app.imageOfMarkUrl) && (
                    <button
                      type="button"
                      onClick={() => {
                        const url =
                          app.pendingImagePreviewUrl ?? app.imageOfMarkUrl;
                        if (!url) return;
                        setFilePreview({
                          url,
                          name: app.pendingImageFile?.name ?? "Mark image",
                          kind: "image",
                        });
                      }}
                      className="px-3 h-8 rounded-[6px] border border-[#D5D3E0] bg-white text-[12px] text-[#2E3037] hover:bg-gray-50"
                    >
                      Preview
                    </button>
                  )}
                  <label
                    className="inline-flex items-center justify-center cursor-pointer px-3 h-8 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] text-[12px] text-[#2E3037] hover:bg-[#EEEEEE]"
                  >
                    {app.imageOfMarkFileKey ||
                    app.imageOfMarkUrl ||
                    app.pendingImagePreviewUrl
                      ? "Replace"
                      : "Upload"}
                    <input
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES}
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleImageSelect(app.rowId, f);
                        e.currentTarget.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addApplicationRow}
          className="self-start inline-flex items-center gap-1 px-3 h-8 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7]"
        >
          + Add More
        </button>
      </div>

      {/* Report-level fields */}
      <div className="flex flex-col gap-5 p-4 rounded-[8px] border border-[#EEEEEE] bg-white">
        <span className="text-[12px] font-medium text-[#222222]">
          Report Details
        </span>

        {/* Applicant | Applicant Address */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-5">
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Applicant</label>
            <input
              type="text"
              value={applicant}
              onChange={(e) => {
                setApplicant(e.target.value);
                setPdf(null);
              }}
              placeholder="Text Here"
              className={INPUT_CLASS}
            />
          </div>
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Applicant Address</label>
            <input
              type="text"
              value={applicantAddress}
              onChange={(e) => {
                setApplicantAddress(e.target.value);
                setPdf(null);
              }}
              placeholder="Applicant Address"
              className={INPUT_CLASS}
            />
          </div>
        </div>

        {/* Applicant Type | District of Applicant */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-5">
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Applicant Type</label>
            <Select
              value={applicantType || undefined}
              onValueChange={(v) => {
                setApplicantType(v);
                setPdf(null);
              }}
            >
              <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                <SelectValue placeholder="Select applicant type" />
              </SelectTrigger>
              <SelectContent>
                {FOLLOW_UP_APPLICANT_TYPES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>District of Applicant</label>
            <Select
              value={districtOfApplicant || undefined}
              onValueChange={(v) => {
                setDistrictOfApplicant(v);
                setPdf(null);
              }}
            >
              <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                <SelectValue placeholder="Select district" />
              </SelectTrigger>
              <SelectContent>
                {FOLLOW_UP_PLACE_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Addressee Designation | (Custom name when NAME) | Form of Address */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-5">
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Addressee</label>
            <Select
              value={addresseeDesignation || undefined}
              onValueChange={(v) => {
                setAddresseeDesignation(v);
                if (v !== "NAME") setAddresseeCustomNameError(false);
                setPdf(null);
              }}
            >
              <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                <SelectValue placeholder="Select addressee" />
              </SelectTrigger>
              <SelectContent>
                {FOLLOW_UP_ADDRESSEE_DESIGNATIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt === "NAME" ? "Custom (enter name)" : opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showCustomNameField && (
            <div className="flex flex-col flex-1 min-w-0 gap-1">
              <label className={LABEL_CLASS}>Addressee Name</label>
              <input
                type="text"
                value={addresseeCustomName}
                onChange={(e) => {
                  setAddresseeCustomName(e.target.value);
                  if (e.target.value.trim()) setAddresseeCustomNameError(false);
                  setPdf(null);
                }}
                placeholder="Mr. John Smith"
                className={`${INPUT_CLASS}${
                  addresseeCustomNameError ? " !border-red-500" : ""
                }`}
              />
              {addresseeCustomNameError && (
                <span className="text-[11px] text-red-500">
                  Addressee custom name is required.
                </span>
              )}
            </div>
          )}
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Form of Address</label>
            <Select
              value={formOfAddress || undefined}
              onValueChange={(v) => {
                setFormOfAddress(v);
                setPdf(null);
              }}
            >
              <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                <SelectValue placeholder="Sir / Madam" />
              </SelectTrigger>
              <SelectContent>
                {FOLLOW_UP_FORM_OF_ADDRESS_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Firm Name | Place — second/third lines of the addressee block */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-5">
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Firm Name</label>
            <input
              type="text"
              value={firmName}
              onChange={(e) => {
                setFirmName(e.target.value);
                setPdf(null);
              }}
              placeholder="Firm Name"
              className={INPUT_CLASS}
            />
          </div>
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Place</label>
            {!isManualPlace ? (
              <Select
                value={place || undefined}
                onValueChange={(v) => {
                  setPlace(v);
                  setPdf(null);
                }}
              >
                <SelectTrigger className="!h-9 !min-h-0 text-[12px]">
                  <SelectValue placeholder="Select place" />
                </SelectTrigger>
                <SelectContent>
                  {FOLLOW_UP_PLACE_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <input
                type="text"
                value={place}
                onChange={(e) => {
                  setPlace(e.target.value);
                  setPdf(null);
                }}
                placeholder="Enter place manually"
                className={INPUT_CLASS}
              />
            )}
            <button
              type="button"
              onClick={() => {
                setIsManualPlace((prev) => {
                  const next = !prev;
                  // Switching back to dropdown: if current `place` isn't a known
                  // district, reset to the first option to keep the Select valid.
                  if (!next) {
                    const isValid = (FOLLOW_UP_PLACE_OPTIONS as readonly string[])
                    .some((d) => d.toLowerCase() === place?.toLowerCase());

                    if (!isValid && place) {
                      setPlace(place); // keep manual value
                    }
                  } 
                  return next;
                });
              }}
              className="self-start text-[11px] md:text-xs text-[#222222] underline underline-offset-2"
            >
              {isManualPlace ? "Use district dropdown" : "Enter place manually"}
            </button>
          </div>
        </div>

        {/* Signatory Name | Signatory Designation */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-5">
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Signatory's Name</label>
            <input
              type="text"
              value={signatoryName}
              onChange={(e) => {
                setSignatoryName(e.target.value);
                setPdf(null);
              }}
              placeholder="Signatory's Name"
              className={INPUT_CLASS}
            />
          </div>
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Signatory's Designation</label>
            <input
              type="text"
              value={signatoryDesignation}
              onChange={(e) => {
                setSignatoryDesignation(e.target.value);
                setPdf(null);
              }}
              placeholder="Signatory's Designation"
              className={INPUT_CLASS}
            />
          </div>
        </div>

        {/* Date of Report | Report No. */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-5">
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Date of Report</label>
            <DateTimePicker
              value={dateOfReport}
              onChange={(d) => {
                setDateOfReport(d);
                setPdf(null);
              }}
              placeholder="Select Date"
              showTime={false}
              buttonClassName="!h-9 !min-h-0 !text-[12px]"
            />
          </div>
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <label className={LABEL_CLASS}>Report No. for Follow up</label>
            <input
              type="text"
              readOnly
              value={reportNo}
              placeholder="Auto-generated on save"
              className={READONLY_CLASS}
            />
          </div>
        </div>
      </div>

      {/* Generate PDF section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-[8px] border border-[#EEEEEE] bg-white">
        <div className="flex items-center gap-2.5 min-w-0">
          <img src={pdfIcon} alt="" className="w-6 h-6 shrink-0" />
          <span className="text-[12px] text-[#222222] truncate">
            {pdf ? pdf.filename : "Generate PDF"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={generatingPdf || saving}
            className="inline-flex items-center justify-center h-9 px-4 rounded-[6px] border border-[#222222] bg-white text-[12px] font-semibold text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {generatingPdf
              ? "Generating…"
              : pdf
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
          <button
            type="button"
            onClick={handlePreviewPdf}
            disabled={!pdf?.downloadUrl}
            className="inline-flex items-center justify-center h-9 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-semibold text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={!pdf?.downloadUrl || downloadingPdf}
            className="inline-flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] font-semibold text-white hover:bg-[#333333] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {downloadingPdf ? "Downloading…" : "Download"}
          </button>
        </div>
      </div>

      <FilePreviewModal
        open={!!filePreview}
        onClose={handleClosePreview}
        name={filePreview?.name ?? ""}
        url={filePreview?.url}
        kind={filePreview?.kind}
      />
    </form>
  );
}
